/**
 * Oscilloscope XY WebGL Visualizer - GPU-accelerated oscilloscope
 * Robust Three.js implementation with proper context handling and optimization
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import * as THREE from 'three';

export class OscilloscopeXYWebGLVisualizer extends BaseVisualizer {
  readonly metadata: VisualizerMetadata = {
    id: 'oscilloscope-xy-webgl',
    name: 'Oscilloscope XY (WebGL)',
    description: 'GPU-accelerated oscilloscope with left/right channel XY plot',
    category: '2d',
    defaultZoom: 1,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: false,
    supportsZooming: false,
    usesWebGL: true,
  };

  // Three.js resources
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.OrthographicCamera | null = null;
  private points: THREE.Points | null = null;
  private fadePlane: THREE.Mesh | null = null;
  private geometry: THREE.BufferGeometry | null = null;
  private material: THREE.PointsMaterial | null = null;
  private spriteTexture: THREE.Texture | null = null;
  private crosshairLines: THREE.LineSegments | null = null;
  private noAudioCanvas: HTMLCanvasElement | null = null;
  private noAudioTexture: THREE.CanvasTexture | null = null;
  private noAudioMesh: THREE.Mesh | null = null;

  // Configuration
  private readonly MAX_SAMPLES = 65536;
  private readonly FRUSTUM_HALF_SIZE = 1.1;
  private readonly decayRate = 0.3;
  private readonly pointSize = 6.5;
  private readonly pointColor = '#00ff00';
  private readonly intensity = 0.3;

  // State
  private isMounted = false;
  private animationId: number = 0;
  private lastWaveformData: { left: Float32Array; right: Float32Array } | null = null;
  private contextLostHandler: ((e: Event) => void) | null = null;
  private contextRestoredHandler: (() => void) | null = null;

  onActivate(): void {
    this.isMounted = true;
  }

  onDeactivate(): void {
    this.isMounted = false;
    this.cleanup();
  }

  render(config: VisualizerConfig): void {
    const { canvas, width, height, waveformData, activeWaves, speed, time } = config;

    if (!this.isMounted || !canvas) return;

    // Initialize Three.js on first render
    if (!this.renderer) {
      this.initializeThreeJS(canvas, width, height);
    }

    if (!this.renderer || !this.scene || !this.camera) return;

    // Update canvas size
    if (this.renderer.domElement.width !== width || this.renderer.domElement.height !== height) {
      this.renderer.setSize(width, height, false);
      this.updateCamera(width, height);
    }

    // Update waveform data - use waveformData if available, otherwise generate from frequency layers
    if (waveformData) {
      this.updateWaveformData(waveformData);
      this.lastWaveformData = waveformData;
      this.hideNoAudioMessage();
    } else if (activeWaves.length > 0) {
      // Generate from frequency layers when no music playing (works with any number of waves, even 1)
      const generatedWaveform = this.generateWaveformFromFrequencyLayers(activeWaves, speed, time);
      this.updateWaveformData(generatedWaveform);
      this.hideNoAudioMessage();
    } else {
      // Clear when no data and show message
      if (this.geometry) {
        this.geometry.setDrawRange(0, 0);
      }
      this.showNoAudioMessage();
    }

    // Render loop: trails then points
    if (this.points && this.fadePlane) {
      // Render fade plane for trails
      this.points.visible = false;
      this.fadePlane.visible = true;
      this.renderer.render(this.scene, this.camera);

      // Render points
      this.fadePlane.visible = false;
      this.points.visible = true;
      this.renderer.render(this.scene, this.camera);
    }
  }

  private generateWaveformFromFrequencyLayers(
    activeWaves: any[],
    speed: number,
    time: number
  ): { left: Float32Array; right: Float32Array } {
    const numSamples = 1024;
    const left = new Float32Array(numSamples);
    const right = new Float32Array(numSamples);

    // Organize waves by channel
    const leftWaves = activeWaves.filter(w => w.channel === 'left');
    const rightWaves = activeWaves.filter(w => w.channel === 'right');
    const bothWaves = activeWaves.filter(w => (w.channel || 'both') === 'both');
    
    // Use left channel waves for X, right channel waves for Y
    // If only one channel exists, the other axis will be zero (normal oscilloscope behavior)
    const xWaves = [...leftWaves, ...bothWaves];
    const yWaves = [...rightWaves, ...bothWaves];

    for (let i = 0; i < numSamples; i++) {
      const t = time + (i / numSamples) * 0.05;

      // Sum all X-channel waves (or 0 if no waves)
      if (xWaves.length > 0) {
        let xVal = 0;
        let xAmpSum = 0;
        xWaves.forEach(w => {
          const wPhase = (w.phase * Math.PI) / 180;
          const wFreq = w.freq * speed;
          const angle = 2 * Math.PI * wFreq * t + wPhase;
          xVal += (w.amp / 100) * this.sampleWaveform(w.waveform, angle);
          xAmpSum += (w.amp / 100);
        });
        left[i] = xAmpSum > 0 ? xVal / xAmpSum : 0;
      } else {
        left[i] = 0;
      }

      // Sum all Y-channel waves (or 0 if no waves)
      if (yWaves.length > 0) {
        let yVal = 0;
        let yAmpSum = 0;
        yWaves.forEach(w => {
          const wPhase = (w.phase * Math.PI) / 180;
          const wFreq = w.freq * speed;
          const angle = 2 * Math.PI * wFreq * t + wPhase;
          yVal += (w.amp / 100) * this.sampleWaveform(w.waveform, angle);
          yAmpSum += (w.amp / 100);
        });
        right[i] = yAmpSum > 0 ? yVal / yAmpSum : 0;
      } else {
        right[i] = 0;
      }
    }

    return { left, right };
  }

  private initializeThreeJS(canvas: HTMLCanvasElement, width: number, height: number): void {
    try {
      // Scene setup
      this.scene = new THREE.Scene();

      // Camera setup
      this.camera = new THREE.OrthographicCamera(
        -this.FRUSTUM_HALF_SIZE,
        this.FRUSTUM_HALF_SIZE,
        this.FRUSTUM_HALF_SIZE,
        -this.FRUSTUM_HALF_SIZE,
        0.1,
        100
      );
      this.camera.position.z = 10;

      // Renderer setup
      this.renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: false,
        preserveDrawingBuffer: true,
        alpha: false,
        depth: false,
        stencil: false,
        powerPreference: 'high-performance'
      });
      this.renderer.autoClear = false;
      this.renderer.setPixelRatio(1);
      this.renderer.setClearColor(0x000000, 1);

      // Create sprite texture for point rendering
      this.spriteTexture = this.createSpriteTexture();

      // Create geometry and material
      this.geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(this.MAX_SAMPLES * 3);
      this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      this.geometry.setDrawRange(0, 0);

      this.material = new THREE.PointsMaterial({
        color: new THREE.Color(this.pointColor),
        size: this.pointSize,
        map: this.spriteTexture,
        transparent: true,
        opacity: this.intensity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false
      });

      // Create points mesh
      this.points = new THREE.Points(this.geometry, this.material);
      this.points.frustumCulled = false;
      this.scene.add(this.points);

      // Create fade plane for trail effect
      const fadeGeo = new THREE.PlaneGeometry(10, 10);
      const fadeMaterial = new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: this.decayRate,
        depthTest: false,
        depthWrite: false
      });
      this.fadePlane = new THREE.Mesh(fadeGeo, fadeMaterial);
      this.fadePlane.position.z = -1;
      this.scene.add(this.fadePlane);

      // Create crosshair lines
      const lineGeo = new THREE.BufferGeometry();
      const linePositions = new Float32Array([
        -2, 0, 0,  2, 0, 0,  // Horizontal line
        0, -2, 0,  0, 2, 0   // Vertical line
      ]);
      lineGeo.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
      const lineMaterial = new THREE.LineBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.01,
        depthTest: false,
        depthWrite: false,
        linewidth: 1
      });
      this.crosshairLines = new THREE.LineSegments(lineGeo, lineMaterial);
      this.scene.add(this.crosshairLines);

      // Context loss handling
      if (!this.contextLostHandler) {
        this.contextLostHandler = (e: Event) => {
          e.preventDefault();
          console.warn("WebGL Context Lost - Attempting restore...");
        };
      }

      if (!this.contextRestoredHandler) {
        this.contextRestoredHandler = () => {
          console.log("WebGL Context Restored");
          if (this.renderer && this.isMounted) {
            this.renderer.setClearColor(0x000000, 1);
          }
        };
      }

      canvas.addEventListener('webglcontextlost', this.contextLostHandler, false);
      canvas.addEventListener('webglcontextrestored', this.contextRestoredHandler, false);
    } catch (e) {
      console.error('Failed to initialize Three.js:', e);
      this.cleanup();
    }
  }

  private createSpriteTexture(): THREE.Texture {
    const texCanvas = document.createElement('canvas');
    texCanvas.width = 64;
    texCanvas.height = 64;
    const ctx = texCanvas.getContext('2d');
    if (ctx) {
      const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
      grad.addColorStop(0.15, 'rgba(255, 255, 255, 1)');
      grad.addColorStop(0.25, 'rgba(255, 255, 255, 0.1)');
      grad.addColorStop(0.5, 'rgba(255, 255, 255, 0.01)');
      grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 64, 64);
    }
    const texture = new THREE.CanvasTexture(texCanvas);
    texture.flipY = false;
    texture.premultiplyAlpha = false;
    return texture;
  }

  private updateWaveformData(waveformData: { left: Float32Array; right: Float32Array }): void {
    if (!this.points || !this.geometry) return;

    const left = waveformData.left;
    const right = waveformData.right;

    // Limit processed points to prevent GPU lag
    const sourceLen = Math.min(left.length, right.length, 1200);

    if (sourceLen < 2) {
      this.geometry.setDrawRange(0, 0);
      return;
    }

    const positionAttribute = this.geometry.attributes.position as THREE.BufferAttribute;
    const positions = positionAttribute.array as Float32Array;

    let outputIndex = 0;
    const interpolationSteps = 9;

    try {
      for (let i = 0; i < sourceLen - 1; i++) {
        const x1 = left[i];
        const y1 = right[i];
        const x2 = left[i + 1];
        const y2 = right[i + 1];

        if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) {
          continue;
        }

        for (let step = 0; step < interpolationSteps; step++) {
          if (outputIndex >= this.MAX_SAMPLES) break;

          const t = step / interpolationSteps;
          positions[outputIndex * 3] = x1 + (x2 - x1) * t;
          positions[outputIndex * 3 + 1] = y1 + (y2 - y1) * t;
          positions[outputIndex * 3 + 2] = 0;

          outputIndex++;
        }
      }

      if (outputIndex > 0) {
        positionAttribute.clearUpdateRanges();
        positionAttribute.addUpdateRange(0, outputIndex * 3);
        positionAttribute.needsUpdate = true;
        this.geometry.setDrawRange(0, outputIndex);
      } else {
        this.geometry.setDrawRange(0, 0);
      }
    } catch (e) {
      console.error('Error updating waveform data:', e);
    }
  }

  private showNoAudioMessage(): void {
    if (this.noAudioMesh && this.scene) {
      this.noAudioMesh.visible = true;
      return;
    }

    if (!this.scene) return;

    // Create canvas texture with "No audio signal" text
    this.noAudioCanvas = document.createElement('canvas');
    this.noAudioCanvas.width = 512;
    this.noAudioCanvas.height = 512;
    const ctx = this.noAudioCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0)';
      ctx.fillRect(0, 0, 512, 512);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.font = '48px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No audio signal', 256, 256);
    }

    this.noAudioTexture = new THREE.CanvasTexture(this.noAudioCanvas);
    const planeGeo = new THREE.PlaneGeometry(2, 0.4);
    const planeMat = new THREE.MeshBasicMaterial({
      map: this.noAudioTexture,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    this.noAudioMesh = new THREE.Mesh(planeGeo, planeMat);
    this.noAudioMesh.position.z = 5;
    this.scene.add(this.noAudioMesh);
  }

  private hideNoAudioMessage(): void {
    if (this.noAudioMesh) {
      this.noAudioMesh.visible = false;
    }
  }

  private updateCamera(width: number, height: number): void {
    if (!this.camera) return;

    // Always keep frustum square (1:1 aspect) regardless of canvas aspect ratio
    // The oscilloscope plot will be centered in the canvas
    this.camera.left = -this.FRUSTUM_HALF_SIZE;
    this.camera.right = this.FRUSTUM_HALF_SIZE;
    this.camera.top = this.FRUSTUM_HALF_SIZE;
    this.camera.bottom = -this.FRUSTUM_HALF_SIZE;
    this.camera.updateProjectionMatrix();
  }

  private cleanup(): void {
    try {
      if (this.geometry) {
        this.geometry.dispose();
        this.geometry = null;
      }
      if (this.material) {
        this.material.dispose();
        this.material = null;
      }
      if (this.spriteTexture) {
        this.spriteTexture.dispose();
        this.spriteTexture = null;
      }
      if (this.noAudioTexture) {
        this.noAudioTexture.dispose();
        this.noAudioTexture = null;
      }
      if (this.noAudioCanvas) {
        this.noAudioCanvas = null;
      }
      if (this.renderer) {
        this.renderer.dispose();
        this.renderer = null;
      }

      this.scene = null;
      this.camera = null;
      this.points = null;
      this.fadePlane = null;
      this.crosshairLines = null;
      this.noAudioMesh = null;
    } catch (e) {
      console.error('Error during cleanup:', e);
    }
  }
}
